import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProperty, resolveAsset } from '../context/PropertyContext';
import { Loader2 } from 'lucide-react';

export default function Login() {
  const { login, error } = useAuth();
  const { property } = useProperty() || {};
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav(loc.state?.from || '/', { replace: true });
  };

  const name = property?.name || 'Front Desk Console';
  const logoUrl = property?.logo_url ? resolveAsset(property.logo_url) : '';

  return (
    <div className="min-h-screen grid lg:grid-cols-2 app-bg">
      <div className="hidden lg:block relative">
        <img
          alt=""
          src="https://images.unsplash.com/photo-1713273253005-f5970d8aba25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxjYXJpYmJlYW4lMjByZXNvcnR8ZW58MHx8fHwxNzg1NTU2MTI3fDA&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1F5A24]/25" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-heading text-4xl leading-tight max-w-md">Welcome to<br/>{name}.</div>
          <div className="mt-2 text-white/85 max-w-sm">The staff console for the reception desk.</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={onSubmit} className="w-full max-w-sm bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-6">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="w-11 h-11 rounded-lg object-contain bg-white border border-[#CFD8CF]" />
            ) : (
              <div className="w-11 h-11 rounded-lg bg-[#2E7D32] text-white grid place-items-center font-heading font-bold text-xl" aria-hidden>
                {name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div data-testid="login-property-name" className="font-heading font-medium text-[#1F5A24] text-lg leading-tight">{name}</div>
              <div className="text-[11px] uppercase tracking-widest text-[#333]/60">Front Desk Console</div>
            </div>
          </div>

          <h1 className="font-heading text-2xl text-[#1F5A24] mb-1">Sign in</h1>
          <p className="text-sm text-[#333]/70 mb-5">Staff access only.</p>

          <label htmlFor="login-email" className="block text-sm font-medium mb-1">Email</label>
          <input
            id="login-email"
            data-testid="login-email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4 bg-white border border-[#CFD8CF] rounded-lg px-4 py-3 min-h-[3rem] focus:ring-2 focus:ring-[#2E7D32] focus:border-[#2E7D32] outline-none"
          />

          <label htmlFor="login-password" className="block text-sm font-medium mb-1">Password</label>
          <input
            id="login-password"
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-4 bg-white border border-[#CFD8CF] rounded-lg px-4 py-3 min-h-[3rem] focus:ring-2 focus:ring-[#2E7D32] focus:border-[#2E7D32] outline-none"
          />

          {error && (
            <div data-testid="login-error" role="alert" className="text-sm text-[#B26A00] mb-3 px-3 py-2 rounded-lg bg-[#FFF3E0] border border-[#B26A00]/30">
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full min-h-[3rem] rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : null}
            Sign in
          </button>

          <div className="my-4 flex items-center gap-3 text-xs text-[#333]/50">
            <div className="flex-1 h-px bg-[#CFD8CF]" />
            <span>or</span>
            <div className="flex-1 h-px bg-[#CFD8CF]" />
          </div>

          <button
            type="button"
            data-testid="login-google"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + '/';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full min-h-[3rem] rounded-lg bg-white hover:bg-[#F5F5F5] border border-[#CFD8CF] text-[#333] font-medium inline-flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59a14.7 14.7 0 010-9.19l-7.98-6.19A23.94 23.94 0 000 24c0 3.87.93 7.53 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </button>

          <p className="mt-3 text-xs text-[#333]/60 text-center">
            Only staff already registered by a manager can use Google sign-in.
          </p>
        </form>
      </div>
    </div>
  );
}
